import React, {useState, useEffect} from 'react';
import { CalendarIcon, UserPlusIcon, CurrencyDollarIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import config from '../../config';
import Helper from "../../helpers";
import {bookUserSession, fetchVenueUsers} from "../../Services/UserService.ts"

const baseUrl = config.apiBaseUrl;

const AddUserToSession = ({handleClose, Values}) => {
    const [sessions, setSessions] = useState([]);
    const [selectedSession, setSelectedSession] = useState(Values.sessionId);
    const [selectedUser, setSelectedUser] = useState("");
    const [sessionCost, setSessionCost] = useState(Values.sessionCost);
    const [users, setUserData] = useState([]);
    const [deductUserToken, setDeductUserToken] = useState(true);
    const [loading, setLoading] = useState(false);

    const updateSelectedSession = (sessionId) => {
        let session = sessions.find(session => session.id === sessionId);
        setSelectedSession(sessionId);
        setSessionCost(session.tokenCost);
    }

    const updateSelectedUser = (userid) => {
        setSelectedUser(userid);
    }


    useEffect(() => {
        // Fetch available sessions
        async function fetchSessions() {
            const token = await Helper.getCookie('wh-jwt');
            let venue = await Helper.getUserVenueId();
            fetch(`${baseUrl}/api/Sessions/available/${venue}?from=${encodeURIComponent(new Date().toISOString())}&to=${encodeURIComponent(new Date(new Date().setDate(new Date().getDate() + 31)).toISOString())}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include'
                }
            )
                .then(response => response.json())
                .then(data => setSessions(data))
                .catch(error => console.error('Error fetching sessions:', error));

            let usersData = await fetchVenueUsers(venue);

            setUserData(usersData);
        }
        fetchSessions();
    }, []);

    const handleBookSession = async () => {
        setLoading(true);
        try {
            let bookedUsersSession = await bookUserSession(selectedUser, selectedSession, deductUserToken);
            if (bookedUsersSession) {
                handleClose();
            }
        } catch (error) {
            console.error('Error booking session:', error);
            alert('Failed to book session. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card-luxury p-6 sm:p-8 max-w-2xl mx-auto animate-slide-up">
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h2 className="text-2xl sm:text-3xl font-display font-bold text-gradient-emerald mb-2">
                        Add User to Session
                    </h2>
                    <div className="flex items-center gap-2 text-sm sm:text-base">
                        <CurrencyDollarIcon className="h-5 w-5 text-emerald-400" />
                        <p className="text-accent-platinum font-body">
                            This session will cost <span className="font-display font-bold text-emerald-300">{sessionCost}</span> token{sessionCost !== 1 ? 's' : ''}
                        </p>
                    </div>
                </div>
                <button
                    onClick={handleClose}
                    className="text-emerald-600/60 hover:text-emerald-300 transition-colors duration-300"
                >
                    <XMarkIcon className="h-6 w-6" />
                </button>
            </div>

            {/* Session Selector */}
            <div className="mb-6">
                <label className="flex items-center gap-2 text-sm font-display font-semibold text-emerald-300/80 mb-3 uppercase tracking-wide">
                    <CalendarIcon className="h-4 w-4" />
                    Select Session
                </label>
                <select
                    value={selectedSession}
                    onChange={(e) => updateSelectedSession(e.target.value)}
                    className="input-field w-full"
                >
                    <option value="">Choose a session...</option>
                    {sessions.map((session) => (
                        <option key={session.id} value={session.id}>
                            {`${session.name} - ${new Date(session.startDate).toLocaleString()}`}
                        </option>
                    ))}
                </select>
            </div>

            {/* User Selector */}
            <div className="mb-6">
                <label className="flex items-center gap-2 text-sm font-display font-semibold text-emerald-300/80 mb-3 uppercase tracking-wide">
                    <UserPlusIcon className="h-4 w-4" />
                    Select User
                </label>
                <select
                    value={selectedUser}
                    onChange={(e) => updateSelectedUser(e.target.value)}
                    className="input-field w-full"
                >
                    <option value="">Choose a user...</option>
                    {users.map((user) => (
                        <option key={user.userId} value={user.userId}>
                            {user.name}
                        </option>
                    ))}
                </select>
            </div>

            {/* Deduct Token Checkbox */}
            <div className="mb-6">
                <label className="glass-luxury rounded-xl p-4 border border-emerald-600/20 bg-gradient-to-br from-luxury-slate/40 to-luxury-obsidian/60 flex items-center gap-3 cursor-pointer hover:border-emerald-600/40 transition-colors">
                    <input
                        type="checkbox"
                        checked={deductUserToken}
                        onChange={(e) => setDeductUserToken(e.target.checked)}
                        className="w-5 h-5 rounded border-emerald-600/30 bg-luxury-charcoal/50 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0 cursor-pointer"
                    />
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <CurrencyDollarIcon className="h-4 w-4 text-emerald-400" />
                            <span className="text-sm sm:text-base font-display font-semibold text-accent-platinum">
                                Deduct User Token
                            </span>
                        </div>
                        <p className="text-xs text-emerald-600/70 font-body mt-1">
                            {deductUserToken 
                                ? `User will be charged ${sessionCost} token${sessionCost !== 1 ? 's' : ''} for this session`
                                : 'User will be added without token deduction (free booking)'}
                        </p>
                    </div>
                    {deductUserToken && (
                        <CheckCircleIcon className="h-5 w-5 text-emerald-400" />
                    )}
                </label>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <button
                    onClick={handleClose}
                    className="btn-outline flex-1 order-2 sm:order-1"
                    disabled={loading}
                >
                    Cancel
                </button>
                <button
                    onClick={handleBookSession}
                    disabled={!selectedSession || !selectedUser || loading}
                    className={`flex-1 order-1 sm:order-2 ${
                        !selectedSession || !selectedUser || loading
                            ? 'bg-luxury-graphite/50 text-accent-platinum/50 cursor-not-allowed border border-emerald-600/20 py-3 px-6 rounded-xl font-display font-semibold transition-all duration-300'
                            : 'btn-primary'
                    }`}
                >
                    {loading ? 'Booking...' : 'Add User to Session'}
                </button>
            </div>
        </div>
    );
};

export default AddUserToSession;
